import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { DRIVER_RELIABILITY_STATUS_MAP } from '@returnmates/client-core/src/constants/routes'
import { DriverReliabilityStatus, Route } from '@returnmates/client-core/src/graphql/generated/api'
import { getRoute as getRouteSelector } from '@returnmates/client-core/src/selectors/admin'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import Button from '@returnmates/ui-core/src/components/Button'
import Modal from '@returnmates/ui-core/src/components/Modal'
import { memo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Select from '../../Select'
import useStyles from '../styles'

interface Props {
  isOpen: boolean
  driverId?: string | null
  onClose: () => void
}

const UpdateDriverReliabilityModal = ({ isOpen, onClose, driverId }: Props) => {
  const currentRoute: Route | null = useSelector(getRouteSelector)
  const dispatch = useDispatch()
  const classes = useStyles()

  const [isLoading, setIsLoading] = useState(false)
  const [driverReliabilityStatus, setDriverReliabilityStatus] = useState<
    DriverReliabilityStatus | string
  >('')

  const handleDriverReliabilityStatus = ({ target: { value } }: { target: { value: string } }) => {
    setDriverReliabilityStatus(value)
  }

  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true)

      await createAsyncAction(
        dispatch,
        actions.createDriverReliability.request({
          input: {
            driverId: driverId,
            routeId: currentRoute?.id,
            status: driverReliabilityStatus,
          },
        }),
      )
    } catch (error) {
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: error,
        }),
      )
    } finally {
      setIsLoading(false)
      onClose()
    }
  }, [dispatch, driverId, currentRoute?.id, driverReliabilityStatus, onClose])

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={448}>
      <div className={classes.modalWrapper}>
        <p className={classes.modalTitle}>Update Driver Reliability</p>
        <p className={classes.modalDescription}>
          Driver reliability is missing or has not been changed recently. If you are reassigning the
          route due to the driver, please specify:
        </p>
        <div className={classes.modalActionsWrapper}>
          <Select
            className={classes.statusSelect}
            value={driverReliabilityStatus || ''}
            values={DRIVER_RELIABILITY_STATUS_MAP}
            onChange={handleDriverReliabilityStatus}
            placeholder="Driver Reliability Status"
            fullWidth
          />
        </div>
        <div className={classes.modalButtons}>
          <Button
            className={classes.modalButton}
            disabled={!driverReliabilityStatus.length}
            isLoading={isLoading}
            onClick={handleSubmit}
            label="Confirm"
          />
          <Button className={classes.modalButton} onClick={onClose} label="Cancel" cancelButton />
        </div>
      </div>
    </Modal>
  )
}

export default memo(UpdateDriverReliabilityModal)
